import React from 'react';
import { format } from 'date-fns';
import { useTranslation, Trans } from 'react-i18next';
import { enUS, es } from 'date-fns/locale';
import classnames from 'classnames';
import Bugsnag from '@bugsnag/js';
import Option from './option';
import classes from './classes.module.scss';
import { purchaseDetailsTypes, paymentSourcesTheme } from '../config/const';
import {
  getMinPrice, getCurrency, isPOW, isPaygAndNotEligibleToPayAsYouGo, getActiveOffer
} from '../config/utils';
import {
  applePayIsSupported, renderFeesLabel, iapIsSupported, googlePayIsSupported
} from '../purchase_details_common/purchase_details_common_utils';
import ProcessApplePay from './process_apple_pay';
import GooglePay from './google_pay';
import { formatLocalCurrency } from '../../app/config/util';

const neetTopBorder = ({ type }) => type !== purchaseDetailsTypes.AFFILIATE_PROMO_POPUP;

const getContainerStyle = ({ type }) => {
  if (neetTopBorder({ type })) return {};
  return { borderTop: 'none' };
};

const renderPriceInfo = ({ isMinPurchase, t }) => {
  if (!isMinPurchase) return null;
  return (
    <div className={ classes.priceInfo }>
      <div className={ classes.priceInfoText }>
        <div className={ classes.priceInfoStar }>*</div>
        {t('chat.purchase_min', { price: formatLocalCurrency(getMinPrice() ?? 0, getCurrency()) })}
      </div>
    </div>
  );
};

const renderAffiliatePromoExpiresAffiliatePromoPopup = ({ type, renderOfferExpires }) => {
  if (type !== purchaseDetailsTypes.AFFILIATE_PROMO_POPUP) return null;
  return renderOfferExpires();
};

const renderPrompt = ({ hasValidOptions, options, t }) =>  {
  if (!(hasValidOptions && options.length > 1)) return null;
  return <span className={ classes.selectOptions }>{ t('purchase.select_option') }</span>;
};

const getOptions = ({ hasValidOptions, options }) => {
  if (hasValidOptions) return options;
  return [{ kind: 'cc', humanKind: 'Credit or Debit Card' }, { kind: 'pp', humanKind: 'PayPal' }];
};

const renderOptions = ({ options, optionProps }) => {
  const { setInAppPaymentOptions } = optionProps;
  const isAdding = options.find((o) => o.adding);
  if (isPOW() && isAdding && setInAppPaymentOptions) {
    setTimeout(() => { setInAppPaymentOptions('iap'); }, 0);
    return null;
  }
  const containerClass = isAdding ? classnames(classes.addPaymentOptionsContainer, classes.topBorder) : classes.addPaymentOptionsContainer;
  return <div className={ containerClass }>{options.map((o, index) => (<Option key={ index } { ...o } { ...optionProps } />))}</div>;
};
const renderContent = ({ hasValidOptions, options, optionProps }) => {
  const { loading, creditCardValidateError } = optionProps;
  if (!hasValidOptions && !loading) return null;
  if (creditCardValidateError) return null;
  const updatedOptions = getOptions({ hasValidOptions, options });
  return renderOptions({ options: updatedOptions, optionProps });
};

const renderNoThanksLink = ({ type, hideAffiliatePromoPopupAction, t }) => {
  if (type !== purchaseDetailsTypes.AFFILIATE_PROMO_POPUP) return null;
  return <button className={ classes.noThanksLink } onClick={ hideAffiliatePromoPopupAction } type="button">{ t('purchase.maybe_later') }</button>;
};

const renderAffiliatePromobuttonText = (buttonTitleText, buttonTitleBonus) => (
  <div className={ classes.buttonTitleContainer }>
    <div className={ classes.buttonTitleText }>
      { buttonTitleText }
      {buttonTitleBonus}
    </div>
  </div>
);

const buttonText = ({
  type, t, requiredCredit, purchaseFees, currency
}) => {
  switch (type) {
    case purchaseDetailsTypes.AFFILIATE_PROMO_POPUP: {
      const activeOffer = getActiveOffer();
      if (!activeOffer) return null;
      const buttonTitleText = t('purchase.affiliate_promo_popup_button_title', { amount: formatLocalCurrency(parseFloat(activeOffer.requiredPurchasesAmount) + purchaseFees, currency) });
      const buttonTitleBonus = t('purchase.affiliate_promo_popup_button_bonus', { amount: formatLocalCurrency(parseFloat(activeOffer.bonus), currency) });
      return renderAffiliatePromobuttonText(buttonTitleText, buttonTitleBonus);
    }
    default: { return t('purchase.purchase_pay', { amount: formatLocalCurrency(requiredCredit + purchaseFees, currency) }); }
  }
};

const logIssueWithPurchase = (options) => {
  try {
    const paymentSources = window.store?.getState().paymentSources;
    if (!paymentSources?.current) return;
    const optionKind = options[0]?.kind;
    const sourceKind = paymentSources?.current?.kind;
    if (options?.length === 1 && sourceKind !== optionKind) {
      Bugsnag.notify('Payment with wrong payment method', (e) => {
        e.severity = 'warning';
      });
    }
  } catch { /* empty */ }
};

const renderBuyButtonContent = ({
  options, forMobileBuyBtn, renderCreditCardProcessing, renderPayPalProcessing, disableBuyButton,
  isMinPurchase, purchaseURL, purchAmount, type, purchaseAmount, bonus, t, requiredCredit, chatType,
  purchaseFees, isPayg, currency, renderCreditCardValidateProcessing, onProcessing,
  isContinue, onPurchaseComplete, cancelChatOnPP, hideAffiliatePromoPopupAction, paymentMethodListAnalyticsEvents, topupFlow, advisorId
}) => {
  const shouldDisableBuyButton = disableBuyButton || ((options[0].kind === 'ap' || options[0].kind === 'am') && !applePayIsSupported()) || (options[0].kind === 'gp' && !googlePayIsSupported());
  logIssueWithPurchase(options);
  if (forMobileBuyBtn) {
    return forMobileBuyBtn({
      disableBuyButton: shouldDisableBuyButton, isPayg, paymentMethodListAnalyticsEvents
    });
  }
  if (renderCreditCardValidateProcessing && isPayg) {
    return renderCreditCardValidateProcessing({
      disableBuyButton: shouldDisableBuyButton, paymentMethodListAnalyticsEvents
    });
  }
  if (options[0].kind === 'cc') {
    return (
      <div className={ classes.buyButtonContainer }>
        {renderCreditCardProcessing(
          disableBuyButton,
          isMinPurchase,
          options[0],
          purchaseURL,
          purchAmount,
          () => (
            <div className={ classnames(classes.buyButton, disableBuyButton ? classes.disabledBuyButton : '', classes[`buyButton${ type }`])  }>
              { buttonText({
                type, purchaseAmount, bonus, t, requiredCredit, purchaseFees, currency
              }) }
            </div>
          )
        )}
      </div>
    );
  }
  if (options[0].kind === 'ap' || options[0].kind === 'am') {
    return (
      <div className={ classes.buyButtonContainer }>
        <ProcessApplePay
          { ...options[0] }
          currentPaymentOption={ options[0] }
          purchaseAmount={ purchAmount }
          disabled={ shouldDisableBuyButton }
          purchaseURL={ purchaseURL }
          chatType={ chatType }
          bonus={ bonus }
          onProcessing={ onProcessing }
          isContinue={ isContinue }
          onPurchaseComplete={ onPurchaseComplete }
          cancelChatOnPP={ cancelChatOnPP }
          type={ type }
          hideAffiliatePromoPopupAction={ hideAffiliatePromoPopupAction }
          purchaseFees={ purchaseFees }
          advisorId={ advisorId }
          topupFlow={ topupFlow }
        >
          <div
            className={ classnames(classes.buyButton, shouldDisableBuyButton ? classes.disabledBuyButton : '', classes[`buyButton${ type }`])  }
            style={ { marginTop: '0px' } }
          >
            {
            buttonText({
              type, purchaseAmount, bonus, t, requiredCredit, purchaseFees
            })
          }
          </div>
        </ProcessApplePay>
      </div>
    );
  }

  if (options[0].kind === 'gp') {
    return (
      <div className={ classes.buyButtonContainer }>
        <GooglePay
          purchaseAmount={ purchAmount }
          currentPaymentOption={ options[0] }
          requiredCredit={ parseFloat(requiredCredit) + purchaseFees }
          paymentSourceId={ options[0].id }
          disabled={ shouldDisableBuyButton }
          purchaseURL={ purchaseURL }
          chatType={ chatType }
          bonus={ bonus }
          onProcessing={ onProcessing }
          isContinue={ isContinue }
          onPurchaseComplete={ onPurchaseComplete }
          cancelChatOnPP={ cancelChatOnPP }
          type={ type }
          hideAffiliatePromoPopupAction={ hideAffiliatePromoPopupAction }
          purchaseFees={ purchaseFees }
          advisorId={ advisorId }
        >
          <div className={ classnames(classes.buyButton, shouldDisableBuyButton ? classes.disabledBuyButton : '', classes[`buyButton${ type }`])  }>
            { buttonText({
              type, purchaseAmount, bonus, t, requiredCredit, purchaseFees
            }) }
          </div>
        </GooglePay>
      </div>
    );
  }
  return (
    <div className={ classes.buyButtonContainer }>
      {renderPayPalProcessing(
        disableBuyButton,
        isMinPurchase,
        options[0],
        purchaseURL,
        purchAmount,
        () => (
          <div className={ classnames(classes.buyButton, disableBuyButton ? classes.disabledBuyButton : '', classes[`buyButton${ type }`])  }>
            { buttonText({
              type, t, requiredCredit, purchaseFees
            }) }
          </div>
        )
      )}
    </div>
  );
};

function PaymentOptions({
  options = [],
  isMinPurchase,
  purchaseURL,
  type,
  purchaseAmount,
  requiredCredit,
  theme = paymentSourcesTheme.purchase,
  loadingOptions,
  loading,
  paymentSources,
  renderPayPalProcessing,
  paymentProcessing,
  paymentMethodListAnalyticsEvents,
  changingSource,
  bonus = 0,
  showPaymentSources,
  chatType,
  onProcessing,
  isContinue = false,
  addCreditCard,
  renderCreditCardProcessing,
  getCurrentLocalization,
  forMobileBuyBtn,
  setInAppPaymentOptions,
  fees = {},
  isPayg = false,
  currency,
  renderCreditCardValidateProcessing,
  creditCardValidateError,
  hideAffiliatePromoPopupAction,
  onPurchaseComplete,
  cancelChatOnPP,
  trackPaymentMethodClickedEvent,
  addSource,
  advisorId,
  topupFlow
}) {
  const { t } = useTranslation();
  const { purchaseFees, purchaseFeesText } = fees;

  const hasValidOptions = options && options.length > 0;
  const addingOptions = hasValidOptions && options.find((e) => !e.adding);

  const renderOfferExpires = () => {
    const locale = { en: enUS, es };
    const expireDate = getActiveOffer()?.expiresAt;
    if (!expireDate) return null;
    const dateDiff = parseInt((new Date(expireDate) - Date.now()) / (1000 * 60 * 60 * 24), 10);
    if (dateDiff > 7) return null;
    const dateString = format(new Date(expireDate), 'MMMM dd, yyyy', { locale: locale[getCurrentLocalization()] });
    return (
      <div className={ classes.offerExpiresDateContainer }>
        <Trans
          i18nKey="purchase.offer_expires_at_midnight_ET"
          components={ {
            bold: <span className={ classes.offerExpiresDateBoldText } />,
            brIfNeed: <br className={ classes.offerExpiresBrIfNeed } />
          } }
          values={ { dateString } }
        />
      </div>
    );
  };

  const renderBuyButton = () => {
    if (!(hasValidOptions && addingOptions)) return null;
    const eligibleToPayAsYouGo = options && options[0]?.eligibleToPayAsYouGo;
    const disableBuyButton = options.find((o) => o.errorText)
      || (isPaygAndNotEligibleToPayAsYouGo({ isPayg, eligibleToPayAsYouGo }))
      || (options.find((o) => o.kind === 'iap') && !iapIsSupported())
      || (loading || loadingOptions);
    const purchAmount = parseFloat(requiredCredit);
    return renderBuyButtonContent({
      options, forMobileBuyBtn, renderCreditCardProcessing, renderPayPalProcessing, bonus, t, onProcessing,
      disableBuyButton, isMinPurchase, purchaseURL, purchAmount, type, purchaseAmount, chatType, isContinue,
      requiredCredit, purchaseFees, isPayg, currency, renderCreditCardValidateProcessing, onPurchaseComplete, cancelChatOnPP, hideAffiliatePromoPopupAction, paymentMethodListAnalyticsEvents, topupFlow, advisorId
    });
  };

  const optionProps = {
    purchaseURL, purchaseAmount: parseFloat(requiredCredit), paymentProcessing, showPaymentSources,
    addCreditCard, theme, loading: loading || loadingOptions, paymentSources, paymentMethodListAnalyticsEvents,
    options, changingSource, setInAppPaymentOptions, isPayg, creditCardValidateError, chatType, trackPaymentMethodClickedEvent, addSource, advisorId
  };

  const renderPaymentOptionsContent = () => (
    <>
      { renderPriceInfo({ isMinPurchase, t }) }
      { renderPrompt({ hasValidOptions, options, t }) }
      { renderContent({ hasValidOptions, options, optionProps }) }
      { renderNoThanksLink({ type, hideAffiliatePromoPopupAction, t }) }
      { renderAffiliatePromoExpiresAffiliatePromoPopup({ type, renderOfferExpires }) }
      { renderBuyButton() }
      { renderFeesLabel({ purchaseFeesText, isPayg }) }
    </>
  );

  const renderPaymentOptions = () => (
    <div className={ classes.container } style={ getContainerStyle({ type }) }>
      { renderPaymentOptionsContent() }
    </div>
  );

  return renderPaymentOptions();
}

export default PaymentOptions;
